<script lang="ts">
import { defineComponent } from 'vue'
import DocumentsDataService from '@/services/DocumentsDataService'
import type Document from '@/types/Document'
import type ResponseData from '@/types/ResponseData'
import type { LaravelPaginatedResponse } from '@/types/ApiResponses'
import * as _ from 'lodash'
import * as FileSaver from 'file-saver'
import dayjs from 'dayjs'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { Table as UiTable, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { TableCard, TableSearch, TablePagination, TableEmptyState } from '@/components/common/table'

export default defineComponent({
  name: 'documents-table',
  components: {
    UiTable,
    TableHeader,
    TableBody,
    TableRow,
    TableCell,
    TableCard,
    TableSearch,
    TablePagination,
    TableEmptyState,
  },
  props: ['hladaj'],
  data() {
    return {
      documents: [] as Document[],
      document: {} as Document,
      filterDocuments: '',
      loaded: false,
      selectedColumn: 'id',
      selectedDirection: 'desc',
      pagination: {} as LaravelPaginatedResponse<Document>['data'],
      pagesRange: 5
    }
  },
  methods: {
    formatDate(dateString: string) {
      const date = dayjs(dateString)
      return date.format('DD.MM.YYYY')
    },
    saveAs(filename: string, blob: Blob) {
      FileSaver.saveAs(blob, filename)
    },
    retrieveDocuments(page: number, type: string) {
      if (this.hladaj != null && this.hladaj != 'all') {
        DocumentsDataService.getAllDocumentsForCompany(this.hladaj, page)
          .then((response: ResponseData) => {
            const responseData = response.data as LaravelPaginatedResponse<Document>['data'];
            this.documents = responseData.data;
            this.pagination = responseData;
            this.loaded = true
          })
          .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
          })
      } else if (this.hladaj == undefined) {
        this.documents = []
        this.loaded = true
      } else {
        if (type == 'prev') {
          page--;
        } else if (type == 'next') {
          page++;
        }

        DocumentsDataService.getAllDocuments(page)
          .then((response: ResponseData) => {
            this.documents = response.data.data.data
            this.pagination = response.data.data
            this.loaded = true
          })
          .catch((e: Error) => {
            this.loaded = true
            toast.error('Chyba: ' + e.message)
          })
      }
    },
    refreshList() {
      this.retrieveDocuments(1, '')
    },
    downloadDocument(id: any) {
      DocumentsDataService.downloadFile(id)
        .then((response: ResponseData) => {
          const byteCharacters = atob(response.data)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: 'application/pdf' })

          this.saveAs('document', blob)
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    setOrderResultsBy(column: any) {
      if (this.selectedColumn == column) {
        if (this.selectedDirection == 'desc') {
          this.selectedDirection = 'asc'
        } else {
          this.selectedDirection = 'desc'
        }
      } else {
        this.selectedDirection = 'asc'
      }
      this.selectedColumn = column
    },
  },
  computed: {
    pages() {
      const currentPage = this.pagination.current_page;
      const lastPage = this.pagination.last_page;
      const startPage = Math.max(1, currentPage - Math.floor(this.pagesRange / 2));
      const endPage = Math.min(lastPage, startPage + this.pagesRange - 1);

      return _.range(startPage, endPage + 1);
    },

    filteredDocuments() {
      return this.documents.filter(document => {
        const serial_number = document.serial_number.toLowerCase()
        const company_name = document.name.toLowerCase()
        const type = document.typeStr.toLowerCase()
        const subtype = document.subtypeStr.toLowerCase()
        const searchTerm = this.filterDocuments.toLowerCase()
        return (
          company_name.includes(searchTerm) ||
          serial_number.includes(searchTerm) ||
          type.includes(searchTerm) ||
          subtype.includes(searchTerm)
        )
      })
    },
    orderedItems() {
      switch (this.selectedColumn) {
        case 'id':
          return _.orderBy(this.filteredDocuments, ['id'], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        case 'company_name':
          return _.orderBy(
            this.filteredDocuments,
            ['company_name'],
            [this.selectedDirection.includes('asc') ? 'asc' : 'desc'],
          )
        case 'serial_number':
          return _.orderBy(
            this.filteredDocuments,
            ['serial_number'],
            [this.selectedDirection.includes('asc') ? 'asc' : 'desc'],
          )
        case 'type':
          return _.orderBy(this.filteredDocuments, ['type'], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        case 'subtype':
          return _.orderBy(this.filteredDocuments, ['subtype'], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        case 'date':
          return _.orderBy(
            this.filteredDocuments,
            ['updated_at'],
            [this.selectedDirection.includes('asc') ? 'asc' : 'desc'],
          )
        default:
          return _.orderBy(this.filteredDocuments, ['id'], ['asc'])
      }
    }
  },
  mounted() {
    this.retrieveDocuments(1, '')
  }
})
</script>

<template>
  <main class="flex-1">
    <div class="py-6">
      <div class="max-w-(--breakpoint-2xl) mx-auto">
        <TableCard title="Doklady">
          <template #controls>
            <div class="flex w-full items-center gap-3 sm:w-auto">
              <div class="flex-1 sm:w-64 sm:flex-initial md:w-72">
                <TableSearch v-model="filterDocuments" placeholder="Hľadať doklady..." />
              </div>
            </div>
          </template>
          <div class="max-w-full overflow-x-auto min-w-0">
            <UiTable class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
              <TableHeader
                class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
              >
                <TableRow>
                  <TableCell is-header scope="col"
                    class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center" :class="{ 'text-brand-500': selectedColumn == 'id' }">
                      ID
                      <button>
                        <svg @click="setOrderResultsBy('id')" xmlns="http://www.w3.org/2000/svg"
                          class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg>
                      </button>
                    </span>
                  </TableCell>
                  <TableCell is-header scope="col" class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center"
                      :class="{ 'text-brand-500': selectedColumn == 'company_name' }">
                      Spoločnosť
                      <button>
                        <svg @click="setOrderResultsBy('company_name')" xmlns="http://www.w3.org/2000/svg"
                          class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg>
                      </button>
                    </span>
                  </TableCell>
                  <TableCell is-header scope="col" class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center"
                      :class="{ 'text-brand-500': selectedColumn == 'serial_number' }">
                      Číslo dokladu
                      <button>
                        <svg @click="setOrderResultsBy('serial_number')" xmlns="http://www.w3.org/2000/svg"
                          class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg>
                      </button>
                    </span>
                  </TableCell>
                  <TableCell is-header scope="col" class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center" :class="{ 'text-brand-500': selectedColumn == 'type' }">
                      Typ
                      <button>
                        <svg @click="setOrderResultsBy('type')" xmlns="http://www.w3.org/2000/svg"
                          class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg>
                      </button>
                    </span>
                  </TableCell>
                  <TableCell is-header scope="col" class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center" :class="{ 'text-brand-500': selectedColumn == 'subtype' }">
                      Podtyp
                      <button>
                        <svg @click="setOrderResultsBy('subtype')" xmlns="http://www.w3.org/2000/svg"
                          class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg>
                      </button>
                    </span>
                  </TableCell>
                  <TableCell is-header scope="col" class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center" :class="{ 'text-brand-500': selectedColumn == 'date' }">
                      Dátum zmeny
                      <button>
                        <svg @click="setOrderResultsBy('date')" xmlns="http://www.w3.org/2000/svg"
                          class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg>
                      </button>
                    </span>
                  </TableCell>
                  <TableCell is-header scope="col" class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 text-right">
                    Stiahnuť PDF
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
                <TableRow class="hover:bg-gray-200 dark:hover:bg-gray-700 bg-white dark:bg-gray-900" v-for="document in orderedItems" :key="document.id">
                  <TableCell class="px-6 py-4 text-sm font-medium text-gray-800 dark:text-gray-200">{{ document.id }}</TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">{{ document.name }}</TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">{{ document.serial_number }}</TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">{{ document.typeStr }}</TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">{{ document.subtypeStr }}</TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">
                    {{ formatDate(document.updated_at.toString()) }}
                  </TableCell>
                  <TableCell class="px-6 py-4 text-sm font-medium text-right dark:text-gray-200">
                    <button @click="downloadDocument(document.id)" class="text-brand-500 hover:text-brand-700">
                      Stiahnuť
                    </button>
                  </TableCell>
                </TableRow>
                <TableEmptyState
                  v-if="orderedItems.length === 0"
                  title="Žiadne doklady neboli nájdené"
                  hint="Skúste upraviť hľadanie."
                  colspan="7"
                />
              </TableBody>
            </UiTable>
          </div>
          <template #footer>
            <TablePagination
              v-if="pagination.last_page > 1"
              :current-page="pagination.current_page"
              :total-pages="pagination.last_page"
              @page-change="retrieveDocuments($event, '')"
            />
          </template>
        </TableCard>
      </div>
    </div>
  </main>
</template>
